<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="download-detail-tag" :field="tagField" />
      <JssRichText class="download-detail-title" :field="detailFields?.title" tag="h1" />
      <div class="download-detail-share">
        <div class="download-detail-share-lt">
          <div class="download-detail-share-lt-item">
            <Icon name="file" />
            <span class="download-detail-share-lt-item-desc">{{ fileInfo }}</span>
          </div>
        </div>
        <div class="download-detail-share-rt">
          <button
            class="download-detail-share-rt-share"
            type="button"
            :aria-label="shareAriaLabel"
            @click="handleShare"
          >
            <Icon name="share" />
          </button>
          <div @click.prevent="handleDownload">
            <siteButton
              v-if="detailFields?.button"
              v-bind="detailFields.button"
              :open-download="false"
              :open-link="false"
            />
          </div>
        </div>
      </div>
      <JssRichText class="download-detail-body" :field="detailFields?.body" tag="div" />
    </ContentGrid>
    <div v-if="showFloatingButton && isSubmitted" class="download-detail-floating">
      <div class="download-detail-floating-btn" @click.prevent="handleDownload">
        <siteButton
          v-if="detailFields?.button"
          v-bind="detailFields.button"
          :open-download="false"
          :open-link="false"
        />
      </div>
    </div>
    <aside
      v-if="!isSubmitted"
      class="download-detail-gate"
      aria-labelledby="download-detail-gate-title"
    >
      <div class="download-detail-gate-inner">
        <p id="download-detail-gate-title" class="download-detail-gate-title">
          <span>Expand to view the remaining this content</span>
          <!-- <span class="download-detail-gate-arrow" aria-hidden="true"></span> -->
        </p>
        <div class="download-detail-gate-actions">
          <button
            class="download-detail-gate-button download-detail-gate-button--primary"
            type="button"
            @click="handleDownload"
          >
            Read more content
          </button>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, inject, watch } from 'vue';
import utils from '@/utils';
import { shareToLinkedIn } from '@/utils/dom-utils';
import BUS_EVENTS from '@/utils/bus-events';
import { setLeadFormSubmitted, isLeadFormSubmitted } from '@/utils/form-utils';

export default {
  name: 'DownloadDetail',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const bus = inject('$' + 'bus');
    const isLinkedInPaidSocialSource = () => {
      if (typeof window === 'undefined') return false;
      const searchParams = new URLSearchParams(window.location.search);
      const utmSource = searchParams.get('utm_source') || '';
      const utmMedium = searchParams.get('utm_medium') || '';
      return utmSource.toLowerCase() === 'linkedin' && utmMedium.toLowerCase() === 'paid_social';
    };
    const canAccessContent = () => isLinkedInPaidSocialSource() || isLeadFormSubmitted('documents');
    const isSubmitted = ref(typeof window !== 'undefined' && canAccessContent());
    const detailFields = computed(() => props.fields || props.page?.fields || {});

    const rootClass = computed(() => [
      ...utils.overall.baseComClass(detailFields.value, 'download-detail'),
      { 'download-detail--locked': !isSubmitted.value },
    ]);

    const rootStyle = computed(() => utils.overall.baseComStyle(detailFields.value));

    const shareAriaLabel = computed(() => 'Share document');

    const showFloatingButton = ref(false);
    const downloadButtonRef = ref(null);
    const PAGE_LOCK_CLASS = 'download-detail-page-locked';

    const updatePageScrollLock = (submitted) => {
      if (typeof document === 'undefined') return;
      document.documentElement.classList.toggle(PAGE_LOCK_CLASS, !submitted);
    };

    const checkDownloadButtonVisibility = () => {
      if (!downloadButtonRef.value) return;
      const rect = downloadButtonRef.value.getBoundingClientRect();
      const isOutOfView = rect.bottom < 0 || rect.top < 0 || rect.left < 0 || rect.right < 0;
      showFloatingButton.value = isOutOfView;
    };

    onMounted(() => {
      downloadButtonRef.value = document.querySelector('.download-detail-share-rt');
      window.addEventListener('scroll', checkDownloadButtonVisibility, { passive: true });
      checkDownloadButtonVisibility();
      isSubmitted.value = canAccessContent();
      updatePageScrollLock(isSubmitted.value);
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', checkDownloadButtonVisibility);
      document.documentElement.classList.remove(PAGE_LOCK_CLASS);
    });

    watch(isSubmitted, updatePageScrollLock);

    const formatFileSize = (bytes) => {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + 'B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'K';
      return (bytes / (1024 * 1024)).toFixed(1) + 'M';
    };

    const getFileExtension = (fileField) => {
      const value = fileField?.value || {};
      const extension = value.extension || value.src?.split(/[?#]/)[0].split('.').pop();
      return extension ? extension.replace(/^\./, '').toUpperCase() : '';
    };

    const tagField = computed(() => {
      if (detailFields.value?.tag?.value) return detailFields.value.tag;
      const resourceText = detailFields.value?.resourceType?.fields?.text?.value;
      return resourceText ? { value: resourceText } : null;
    });

    const fileInfo = computed(() => {
      const file = detailFields.value?.file;
      if (!file) return '';
      const type = getFileExtension(file);
      const size = formatFileSize(file.value?.size);
      if (!type && !size) return '';
      return [type, size].filter(Boolean).join(' - ');
    });

    const getShareUrl = () => {
      if (detailFields.value?.shareLink?.value?.href)
        return detailFields.value.shareLink.value.href;
      if (typeof window === 'undefined') return '';
      return window.location.href;
    };

    const handleShare = () => {
      const url = getShareUrl();
      shareToLinkedIn(url);
    };

    const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '');

    const getFieldValue = (field, fallback = '') => field?.value ?? fallback;

    const getButtonFields = () =>
      detailFields.value?.button?.fields || detailFields.value?.button || {};

    const getDownloadUrl = () => {
      const buttonFields = getButtonFields();
      return (
        detailFields.value?.file?.value?.src ||
        buttonFields?.file?.value?.src ||
        buttonFields?.link?.value?.href ||
        ''
      );
    };

    const handleDownload = () => {
      const routeFile = detailFields.value?.file;
      const buttonFields = getButtonFields();
      const buttonFile = buttonFields?.file;
      const file = routeFile?.value?.src ? routeFile : buttonFile;
      const downloadUrl = getDownloadUrl();

      if (isSubmitted.value) {
        if (downloadUrl) {
          const target = buttonFields?.link?.value?.target || '_blank';
          window.open(downloadUrl, target);
        }
        return;
      }

      const title = stripHtml(
        getFieldValue(detailFields.value?.title) || props.page?.displayName || ''
      );
      const description = stripHtml(
        getFieldValue(detailFields.value?.description) ||
          getFieldValue(detailFields.value?.pageTitle)
      );
      const fileType = getFileExtension(file);
      const fileSize = formatFileSize(file?.value?.size);

      bus?.emit(BUS_EVENTS.FORM_OPEN, {
        downloadUrl,
        resource: {
          id: props.rendering?.dataSource || props.page?.itemId,
          title,
          description,
          fileType,
          fileSize,
          downloadLink: {
            href: downloadUrl,
            target: '_blank',
          },
        },
        onSuccess: () => {
          setLeadFormSubmitted('documents');
          isSubmitted.value = true;
        },
      });
    };

    return {
      detailFields,
      tagField,
      rootClass,
      rootStyle,
      fileInfo,
      shareAriaLabel,
      getShareUrl,
      handleShare,
      handleDownload,
      showFloatingButton,
      isSubmitted,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.download-detail {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-tag {
    font-size: 12px;
    line-height: 1.2;
    color: $white;
    padding: 3px 6px;
    background: $blue-night;
    border-radius: 2px;
    display: inline-block;
  }

  &-title {
    margin-top: 16px;
  }

  &-share {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;

    &-lt {
      display: flex;
      align-items: center;
      gap: 16px;
      color: #656565;

      &-item {
        display: flex;
        align-items: center;
        gap: 4px;
        &-desc {
          font-size: 14px;
          line-height: 1.4;
          color: #656565;
        }
      }
    }

    &-rt {
      display: flex;
      align-items: center;
      gap: 8px;

      &-share {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        justify-self: end;
        width: 40px;
        height: 40px;
        padding: 0;
        color: #111111;
        background: transparent;
        border: 0;
        cursor: pointer;

        svg {
          width: 32px;
          height: 32px;
        }

        &:hover,
        &:focus-visible {
          color: #739b78;
          outline: none;
        }
      }

      &-button {
        margin-left: 16px;
      }
    }
  }

  &-body {
    margin-top: 24px;
    padding-bottom: 16px;
  }

  &--locked {
    height: calc(100vh - #{mobile-header-height()});
    min-height: 480px;
    overflow: hidden;

    .download-detail-body {
      max-height: calc(100vh - 330px);
      overflow: hidden;
    }
  }

  &-gate {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 11;
    min-height: 190px;
    padding: 0 16px 24px;
    background: $white;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 100%;
      left: 0;
      height: 96px;
      pointer-events: none;
      background: linear-gradient(180deg, rgba($white, 0), $white 82%);
    }

    &-inner {
      width: min(100%, 840px);
      margin: 0 auto;
      padding-top: 28px;
      border-top: 1px solid #dedede;
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin: 0;
      color: #739b78;
      font-size: 13px;
      line-height: 1.4;
      text-align: center;
    }

    &-arrow {
      width: 8px;
      height: 8px;
      border-right: 1px solid currentColor;
      border-bottom: 1px solid currentColor;
      transform: translateY(-2px) rotate(45deg);
    }

    &-actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin-top: 30px;
    }

    &-button {
      min-width: 154px;
      min-height: 42px;
      padding: 10px 20px;
      border: 1px solid #4d4d4d;
      border-radius: 3px;
      background: $white;
      color: #4d4d4d;
      font: inherit;
      font-size: 13px;
      line-height: 1.2;
      cursor: pointer;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease;

      &--primary,
      &:hover,
      &:focus-visible {
        border-color: #739b78;
        background: #739b78;
        color: $white;
        outline: none;
      }
    }
  }

  &-floating {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: $white;
    padding: 16px 0;
    z-index: 12;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

    &-btn {
      display: flex;
      justify-content: center;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-title {
      font-size: 32px;
      margin-top: 24px;
    }

    &-share {
      margin-top: 30px;

      &-rt {
        gap: 24px;
      }
    }

    &-body {
      margin-top: 40px;
    }

    &--locked {
      height: calc(100vh - #{desktop-header-height()});

      .download-detail-body {
        max-height: calc(100vh - 390px);
      }
    }

    &-gate {
      min-height: 214px;
      padding: 0 24px 38px;

      &-inner {
        padding-top: 38px;
      }

      &-actions {
        gap: 14px;
        margin-top: 32px;
      }

      &-button {
        min-width: 170px;
      }
    }
  }
}

html.download-detail-page-locked,
html.download-detail-page-locked body {
  height: 100%;
  overflow: hidden;
}
</style>
